import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { ScanLine, X, CheckCircle2, Loader2 } from "lucide-react";

/**
 * BarcodeScanner – kleine Icon-Button, der bei Klick einen Modalen Kamera-Dialog
 * öffnet und alle gängigen Code-Typen scannt (EAN, Code-128, QR, Data-Matrix, ...).
 * Auf erfolgreichem Scan wird der String an `onScan(code)` übergeben.
 */
const BarcodeScanner = ({ onScan, title = "Barcode / QR-Code scannen", description, buttonVariant = "outline", buttonSize = "icon" }) => {
  const [open, setOpen] = useState(false);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState(null);
  const [lastDetected, setLastDetected] = useState(null);
  const html5QrRef = useRef(null);
  const containerId = useRef(`bcs-${Math.random().toString(36).slice(2)}`);
  const onScanRef = useRef(onScan);

  // Keep latest onScan in a ref so the start/stop effect does not re-run on every parent render
  useEffect(() => { onScanRef.current = onScan; }, [onScan]);

  // Start camera when dialog opens
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setError(null);
    setLastDetected(null);

    const waitForElement = (id, maxMs = 1000) => new Promise((resolve, reject) => {
      const start = Date.now();
      const tick = () => {
        if (document.getElementById(id)) return resolve();
        if (Date.now() - start >= maxMs) return reject(new Error("scan container missing"));
        requestAnimationFrame(tick);
      };
      tick();
    });

    const start = async () => {
      try {
        // Wait until the container div is actually mounted (Dialog portal may need a tick)
        await waitForElement(containerId.current);
        if (cancelled) return;

        const html5 = new Html5Qrcode(containerId.current);
        html5QrRef.current = html5;

        const cameras = await Html5Qrcode.getCameras();
        if (cancelled) return;
        if (!cameras || cameras.length === 0) {
          setError("Keine Kamera gefunden.");
          return;
        }
        // Prefer back camera ("environment")
        const back = cameras.find((c) =>
          /back|rear|environment/i.test(c.label || "")
        );
        const cameraId = (back || cameras[cameras.length - 1]).id;

        await html5.start(
          cameraId,
          {
            fps: 10,
            qrbox: (vw, vh) => {
              const min = Math.min(vw, vh);
              const size = Math.floor(min * 0.7);
              return { width: size, height: Math.floor(size * 0.6) };
            },
            aspectRatio: 1.7777,
          },
          (decodedText) => {
            if (cancelled) return;
            setLastDetected(decodedText);
            // Stop & emit asynchronously to avoid race
            (async () => {
              try { await html5.stop(); } catch { /* noop */ }
              try { await html5.clear(); } catch { /* noop */ }
              onScanRef.current?.(decodedText);
              setOpen(false);
            })();
          },
          () => { /* scan error per frame – ignore noisy logs */ }
        );
        if (!cancelled) setScanning(true);
      } catch (e) {
        const msg = e?.message || String(e);
        if (/permission|NotAllowed/i.test(msg)) {
          setError("Kamerazugriff verweigert. Bitte Browser-Berechtigung erlauben.");
        } else if (/NotFound|secure context|getUserMedia/i.test(msg)) {
          setError("Kamera nicht verfügbar (HTTPS erforderlich).");
        } else if (/scan container missing/.test(msg)) {
          setError("Scanner konnte nicht initialisiert werden. Bitte erneut versuchen.");
        } else {
          setError(msg);
        }
      }
    };

    start();

    return () => {
      cancelled = true;
      const inst = html5QrRef.current;
      if (inst) {
        (async () => {
          try {
            if (inst.isScanning) await inst.stop();
          } catch { /* noop */ }
          try { await inst.clear(); } catch { /* noop */ }
        })();
      }
      setScanning(false);
    };
  }, [open]);

  return (
    <>
      <Button
        type="button"
        variant={buttonVariant}
        size={buttonSize}
        onClick={(e) => { e.stopPropagation(); setOpen(true); }}
        className="shrink-0"
        title={title}
        data-testid="barcode-scan-btn"
      >
        <ScanLine className="w-4 h-4" />
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-[520px]" data-testid="barcode-scanner-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ScanLine className="w-5 h-5 text-primary" /> {title}
            </DialogTitle>
            {description && <DialogDescription>{description}</DialogDescription>}
          </DialogHeader>

          <div className="relative bg-black rounded-md overflow-hidden" style={{ minHeight: "300px" }}>
            <div id={containerId.current} className="w-full" />
            {!scanning && !error && (
              <div className="absolute inset-0 flex items-center justify-center text-white text-sm">
                <Loader2 className="w-5 h-5 animate-spin mr-2" /> Kamera wird gestartet...
              </div>
            )}
            {error && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/70 text-white p-4 text-center">
                <X className="w-8 h-8 mb-2 text-red-400" />
                <p className="text-sm">{error}</p>
              </div>
            )}
            {lastDetected && (
              <div className="absolute top-2 left-2 right-2 bg-emerald-600 text-white px-3 py-2 rounded text-sm flex items-center gap-2">
                <CheckCircle2 className="w-4 h-4" />
                <span className="font-mono">{lastDetected}</span>
              </div>
            )}
          </div>

          <p className="text-xs text-muted-foreground text-center">
            Halte den Barcode oder QR-Code ruhig vor die Kamera. Die Erkennung erfolgt automatisch.
          </p>

          <div className="flex justify-end">
            <Button variant="outline" onClick={() => setOpen(false)}>Schließen</Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default BarcodeScanner;
